import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileContext } from '@/features/context/ProfileContext';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { getDeletedSCAWallets } from '@/store/actions/sca.actions';

import { selectSCADeletedState, selectSCAWallets, selectSCAState } from '@/store/selectors';

export const FILTER = {
  ACTIVE: 'active',
  DELETED: 'deleted',
};

export type TFilterStatus = typeof FILTER.ACTIVE | typeof FILTER.DELETED;

export function useSCAList() {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const [activePage, setActivePage] = useState<number>(0);
  const [filterStatus, setFilterStatus] = useState<TFilterStatus>(FILTER.ACTIVE);

  const { user } = useProfileContext();

  const statusOptions = [FILTER.ACTIVE, FILTER.DELETED].map((status) => ({
    label: t(`pages.currentProfiles.profile.sca.filter.${status}`),
    value: status,
  }));

  const scaWallets = useAppSelector((state) => selectSCAWallets(state, user.userId));
  const isLoading = useAppSelector(
    (state) => selectSCAState(state, user.userId)?.isLoading || selectSCADeletedState(state, user.userId)?.isLoading,
  );
  const deletedScaWallets = useAppSelector((state) => selectSCADeletedState(state, user.userId)?.state)?.elements;
  const scaWalletCursor = useAppSelector((state) => selectSCADeletedState(state, user.userId)?.state)?.cursor;

  const handleCursor = useCallback(
    (cursor?: string) => {
      dispatch(getDeletedSCAWallets(user.userId, cursor));
    },
    [dispatch, user.userId],
  );

  const handleFilterStatus = (value: TFilterStatus) => {
    setFilterStatus(value);
    if (value === FILTER.DELETED) {
      dispatch(getDeletedSCAWallets(user.userId, undefined, 30000));
    }
  };

  const selectedWallets = filterStatus === FILTER.ACTIVE ? scaWallets : deletedScaWallets;

  return {
    activePage,
    handlePageChange: (page: number) => setActivePage(page),
    isLoading,
    selectedWallets,
    statusOptions,
    filterStatus,
    handleFilterStatus,
    onResetFilter: () => setFilterStatus(FILTER.ACTIVE),
    cursor: filterStatus === FILTER.DELETED ? scaWalletCursor : undefined,
    handleCursor,
  };
}
